import modulesJson from "./generated/modules.json";
import {
  RARITY_ORDER,
  type ModuleDefinition,
  type ModuleRarity,
} from "../modules/ProjectileModule.js";
import type { ModuleSlotType } from "../models/ModuleTypes.js";
import type { RarityDef } from "../systems/GoldModuleBox.js";
import { parseModuleEffect, type ModuleEffectData } from "../modules/ModuleEffect.js";
import { parsePieces } from "./modulePieces.js";

/**
 * 모듈 카탈로그 — 메타(id/이름/설명/슬롯/등급/enable) + **등급별 효과**는 `data/modules.tsv`가 SSoT.
 * `npm run export:data` → `generated/modules.json`을 enable 필터 후 effect{Grade} 컬럼(mini-DSL)을
 * parseModuleEffect로 등급별 ModuleEffectData로 변환한다. `enable=false` 모듈은 여기서 제외되어
 * 상점/도감/그리드 전체에서 자동 미노출된다.
 *
 * 효과는 코드 하드코딩(구 *_APPLY Record)을 폐기하고 데이터로 이관 — 등급마다 효과를 tsv에서 세부 조정 가능.
 * displayName/description은 i18n(`t(\`module.<id>.name\`)`)이 실제 UI SSoT (TSV값은 개발 참조용).
 */
export const ALL_MODULE_DEFINITIONS: readonly ModuleDefinition[] = modulesJson
  .filter((m) => m.enable)
  .map((m) => {
    const effects: Partial<Record<ModuleRarity, ModuleEffectData>> = {};
    // 등급별 effect{Rarity} 컬럼을 RARITY_ORDER 순회로 동적 매핑 — 신규 등급(transcendent 등) 추가 시
    // 코드 수정 0, modules.tsv에 effect{Rarity} 컬럼만 추가하면 자동 인식(데이터 제어).
    for (const rarity of RARITY_ORDER) {
      const col = `effect${rarity.charAt(0).toUpperCase()}${rarity.slice(1)}` as keyof typeof m;
      const raw = m[col];
      if (typeof raw === "string" && raw) effects[rarity] = parseModuleEffect(raw);
    }
    return {
      id: m.id,
      displayName: m.displayName,
      description: m.description,
      slotType: m.slotType as ModuleSlotType,
      rarity: m.rarity as ModuleRarity,
      // pieces 컬럼은 `shapeId:footprintId|...` string — parsePieces로 조각 풀 배열로(빈 값=[FALLBACK_PIECE]).
      pieces: parsePieces(m.pieces ?? ""),
      effects,
    } satisfies ModuleDefinition;
  });

/**
 * 이 모듈이 **등장 가능한 등급 목록** (낮음→높음) — effect{Grade} 칸이 채워진 등급 = 그 등급으로 존재 가능.
 * 첫 항목 = 최소 등급(예: split=epic부터, chain=legendary만). 획득 등급 추첨/최소 등급 판정의 SSoT.
 */
export function moduleRaritiesOf(def: ModuleDefinition): ModuleRarity[] {
  return RARITY_ORDER.filter((r) => def.effects[r] !== undefined);
}

/**
 * 카탈로그를 **(id, rarity) 엔트리로 펼친 등급 추첨 풀** — 같은 모듈이 등장 가능한 모든 등급으로 중복 등장한다.
 * GoldModuleBox/RewardResolver가 "등급 먼저 → 등급 내 모듈 균등" 추첨에 사용(굴린 등급이 인스턴스에 반영).
 * effect 칸이 빈 등급은 자동 제외되어 모듈별 최소 등급이 데이터로 표현된다.
 */
export function expandedModulePool(
  defs: readonly ModuleDefinition[] = ALL_MODULE_DEFINITIONS,
): RarityDef[] {
  return defs.flatMap((d) => moduleRaritiesOf(d).map((rarity) => ({ id: d.id, rarity })));
}
